import { useQuery } from "@tanstack/react-query";

export type CefrProgress = {
  cefrPercent: number;
  skillsTotal: number;
  skillsMastered: number;
  skillsInProgress: number;
  skillsUntouched: number;
  medianSigma: number;
};

/**
 * Honest CEFR-progress tile, backed by /stats/cefr-progress (which
 * reads the Bayesian θ-belief). A skill counts as "mastered" only when
 * σ ≤ 0.32 AND μ ≥ logit(0.85) — calibrated, not inflated. See
 * docs/engine-changes-2026-05-23.md § Bayesian θ-belief.
 *
 * Renders nothing until the first /stats/cefr-progress response — the
 * tile is information dense and a flash-of-zeros while loading would
 * read as "you've made no progress."
 *
 * Used on both /home and /progress; extracted into a shared component
 * so the two surfaces don't drift.
 */
export function CefrProgressCard({ language }: { language: string }) {
  const q = useQuery({
    queryKey: ["cefr-progress", language],
    queryFn: async (): Promise<CefrProgress> => {
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || "/api"}/stats/cefr-progress?language=${encodeURIComponent(
          language,
        )}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`/stats/cefr-progress ${res.status}`);
      return (await res.json()) as CefrProgress;
    },
    staleTime: 60_000,
  });

  if (!q.data) return null;
  const d = q.data;
  const pctClamped = Math.max(0, Math.min(100, d.cefrPercent));
  const seenPct =
    d.skillsTotal > 0
      ? Math.round(
          (100 * (d.skillsMastered + d.skillsInProgress)) / d.skillsTotal,
        )
      : 0;

  return (
    <section className="mb-12 sm:mb-16 border border-ink-100 bg-paper p-4 sm:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-3 mb-2">
        <div>
          <div
            className="eyebrow"
            title="Percent of A1+A2+B1 skills the system is confident you've mastered. A skill counts as mastered when you've answered enough times to keep the model's memory probability (P) consistently high."
          >
            CEFR progress · A1 → B1
          </div>
          <div className="font-serif text-3xl sm:text-4xl text-ink-900 tabular-nums">
            {pctClamped.toFixed(0)}
            <span className="text-ink-400 text-base">%</span>
          </div>
          <div className="text-xs text-ink-400 mt-1 tabular-nums">
            {d.skillsMastered} mastered · {d.skillsInProgress} in progress ·{" "}
            {d.skillsUntouched} untouched
          </div>
        </div>
        <div className="sm:text-right">
          <div className="eyebrow">Confidence</div>
          <div className="font-serif text-2xl sm:text-3xl text-ink-900">
            {d.medianSigma <= 0.32
              ? "High"
              : d.medianSigma <= 0.6
                ? "Calibrating"
                : "Just starting"}
          </div>
          <div className="text-xs text-ink-400 mt-1">
            {d.medianSigma <= 0.32
              ? "The system has a sharp read on your level."
              : d.medianSigma <= 0.6
                ? "Still figuring out where you sit — keep practicing."
                : "Needs more attempts before we can place you."}
          </div>
        </div>
      </div>

      {/* Stacked bar: mastered + in-progress + untouched. The numbers
          on top tell the precise story; the bar is just a glance. */}
      <div className="mt-4 flex h-2 w-full overflow-hidden border border-ink-100 bg-ink-50">
        <div
          className="bg-ink-900 transition-all"
          style={{
            width: `${(100 * d.skillsMastered) / Math.max(1, d.skillsTotal)}%`,
          }}
          title={`${d.skillsMastered} mastered`}
        />
        <div
          className="bg-ink-400 transition-all"
          style={{
            width: `${(100 * d.skillsInProgress) / Math.max(1, d.skillsTotal)}%`,
          }}
          title={`${d.skillsInProgress} in progress`}
        />
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-ink-400 tabular-nums">
        <span>{seenPct}% touched</span>
        <span>{d.skillsTotal} target skills</span>
      </div>
    </section>
  );
}
